import {
  CheckIcon,
  CopyIcon,
  LoaderIcon,
  SquareArrowOutDownRightIcon,
} from "lucide-react";
import { FC, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetDoclist } from "~/api/chat/chat";
import {
  getRawCustomReport,
  useExportAsyncCustomReport,
} from "~/api/custom-reports/custom-reports";
import type { ReportSectionMeta } from "~/api/model";
import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ExportDocListDialog } from "~/features/chat/components/modals/ExportDocListDialog";
import { ReportExportModal } from "~/features/chat/components/modals/ReportExportModal";
import {
  EMAIL_EXPORT_MAX_CHARACTERS,
  EXPORT_LIST,
  EXPORT_TYPE,
  EXPORT_WITH_DOCLIST_MAX_PAGES,
  REPORT_EXPORT_LIST,
  REPORT_EXPORT_TYPE,
} from "~/features/chat/constants";
import {
  convertSourcesToBatesText,
  downloadStringAsFile,
  downloadSummaryAsDocx,
  downloadSummaryAsPdf,
  sendSummaryWithEmail,
  stripReferenceLinksFromContent,
} from "~/features/chat/helpers";
import { useChatDocumentParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useCopyContent } from "~/hooks/useCopyContent";
import { trackEvent } from "~/utils/analytics";
import { normalizeLiteralNewlines } from "~/utils/markdownTerms";

type ReportCopyExportActionsProps = {
  content: string;
  reportId?: string;
  reportSections?: ReportSectionMeta[];
  fileName: string;
  title?: string;
  disabled?: boolean;
  buttonSize?: "sm" | "mini";
  copyEventName?: string;
  exportOpenEventName?: string;
  exportEventName?: string;
  templateName?: string;
  exportMode?: "sync" | "async";
  caseInfo?: {
    caseName: string;
    dateOfBirth: string;
    dateOfLoss: string;
    claimNumber: string;
  };
  isSourcesAddedToRawReport?: boolean;
};

export const ReportCopyExportActions: FC<ReportCopyExportActionsProps> = ({
  content,
  reportId,
  reportSections,
  fileName,
  title,
  disabled,
  buttonSize = "sm",
  copyEventName,
  exportOpenEventName,
  exportEventName,
  templateName,
  exportMode = "sync",
  caseInfo,
  isSourcesAddedToRawReport = false,
}) => {
  const { organizationId, caseId: projectId } = useChatDocumentParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);
  const { data: docList } = useGetDoclist(organizationId, projectId);
  const totalDoclistPages = useMemo(
    () =>
      docList?.tree?.reduce(
        (sum, item) =>
          sum + item.sections.reduce((s, sec) => s + sec.pageCount, 0),
        0
      ) ?? 0,
    [docList]
  );
  const { isCopied, handleCopy } = useCopyContent();
  const { toast } = useToast();
  const intl = useIntl();
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [nameOfExportedFile, setNameOfExportedFile] = useState("");

  const { mutateAsync: exportAsyncCustomReport, isPending: isExporting } =
    useExportAsyncCustomReport();

  const fetchRawContent = useCallback(async (): Promise<string> => {
    if (!reportId) return content;

    const rawReport = await getRawCustomReport(
      organizationId,
      projectId,
      reportId
    );

    let mainContent: string;

    if (rawReport.reportSections && rawReport.reportSections.length > 0) {
      mainContent = rawReport.reportSections
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(
          (section) =>
            `## ${section.title}\n\n${normalizeLiteralNewlines(section.rawContent ?? "")}`
        )
        .join("\n\n");
    } else {
      mainContent = normalizeLiteralNewlines(rawReport.text ?? "");
    }

    return mainContent;
  }, [organizationId, reportId, projectId, content]);

  const resolveAndAct = async (action: (resolved: string) => void) => {
    setIsLoadingContent(true);
    try {
      const resolved = await fetchRawContent();
      action(convertSourcesToBatesText(resolved));
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleCopyClick = () => {
    if (copyEventName) {
      trackEvent({ eventName: copyEventName });
    }
    resolveAndAct((c) => handleCopy(c));
  };

  const handleExport = (
    type: EXPORT_TYPE | REPORT_EXPORT_TYPE,
    exportOptions?: { hideReferences?: boolean }
  ) => {
    if (exportEventName) {
      trackEvent({
        eventName: exportEventName,
        properties: {
          type,
          templateName: templateName ?? undefined,
          hasSources: !!reportSections && reportSections.length > 0,
        },
      });
    }

    if (reportId) {
      if (
        type === REPORT_EXPORT_TYPE.ASYNC_PDF ||
        type === REPORT_EXPORT_TYPE.ASYNC_DOCX ||
        type === REPORT_EXPORT_TYPE.ASYNC_PDF_WITH_DOCLIST ||
        type === REPORT_EXPORT_TYPE.ASYNC_DOCX_WITH_DOCLIST
      ) {
        const isWithDoclist =
          type === REPORT_EXPORT_TYPE.ASYNC_PDF_WITH_DOCLIST ||
          type === REPORT_EXPORT_TYPE.ASYNC_DOCX_WITH_DOCLIST;
        const extension =
          type === REPORT_EXPORT_TYPE.ASYNC_PDF ||
          type === REPORT_EXPORT_TYPE.ASYNC_PDF_WITH_DOCLIST
            ? "pdf"
            : "docx";
        const resolvedFileName = fileName.endsWith(`.${extension}`)
          ? fileName
          : `${fileName}.${extension}`;

        exportAsyncCustomReport(
          {
            organizationId,
            projectId,
            reportId,
            data: {
              format: extension,
              ...(isWithDoclist && { isDoclistIncluded: true }),
              hideReferences: exportOptions?.hideReferences ?? false,
            },
          },
          {
            onSuccess: () => {
              setNameOfExportedFile(resolvedFileName);
              setIsExportDialogOpen(true);
            },
            onError: () => {
              toast({
                variant: "destructive",
                title: intl.formatMessage({
                  id: "error",
                  defaultMessage: "Error",
                }),
                description: intl.formatMessage({
                  id: "report.export.async.error",
                  defaultMessage: "Failed to start export. Please try again.",
                }),
              });
            },
          }
        );
        return;
      }
    }

    const exportTitle = title || "Summary";
    resolveAndAct((c) => {
      switch (type) {
        case EXPORT_TYPE.DOCX:
        case REPORT_EXPORT_TYPE.DOCX:
          downloadSummaryAsDocx(
            c,
            fileName,
            exportTitle,
            settings?.fontFamily,
            settings?.lineSpacing,
            caseInfo,
            settings?.logoUrl
          );
          break;
        case EXPORT_TYPE.PLAIN_TEXT:
        case REPORT_EXPORT_TYPE.PLAIN_TEXT:
          const strippedContent =
            c && c.length > 0 ? stripReferenceLinksFromContent(c) : c;
          downloadStringAsFile(strippedContent, fileName);
          break;
        case EXPORT_TYPE.PDF:
        case REPORT_EXPORT_TYPE.PDF:
          downloadSummaryAsPdf(
            c,
            fileName,
            exportTitle,
            caseInfo,
            settings?.fontFamily,
            settings?.lineSpacing,
            settings?.logoUrl,
            settings?.footnote
          );
          break;
        case EXPORT_TYPE.EMAIL:
        case REPORT_EXPORT_TYPE.EMAIL:
          const strippedContentEmail =
            c && c.length > 0 ? stripReferenceLinksFromContent(c) : c;
          sendSummaryWithEmail(strippedContentEmail, fileName);
          break;
      }
    });
  };

  const handleOpenExportModal = () => {
    if (disabled) return;
    if (exportOpenEventName) {
      trackEvent({ eventName: exportOpenEventName });
    }
    setIsExportModalOpen(true);
  };

  const handleDropdownExport = (type: EXPORT_TYPE | REPORT_EXPORT_TYPE) => {
    if (exportOpenEventName) {
      trackEvent({ eventName: exportOpenEventName });
    }
    handleExport(type);
  };

  const hasDoclist =
    isSourcesAddedToRawReport &&
    totalDoclistPages > 0 &&
    totalDoclistPages <= EXPORT_WITH_DOCLIST_MAX_PAGES;

  const availableExportOptions = useMemo(() => {
    const isEmailExportDisabled = content.length > EMAIL_EXPORT_MAX_CHARACTERS;

    if (reportId) {
      return REPORT_EXPORT_LIST.filter((item) => {
        if (item.type === REPORT_EXPORT_TYPE.EMAIL && isEmailExportDisabled) {
          return false;
        }

        // Reports always use async exports, so filter out sync DOCX and PDF
        if (
          item.type === REPORT_EXPORT_TYPE.DOCX ||
          item.type === REPORT_EXPORT_TYPE.PDF
        ) {
          return false;
        }

        // If exportMode is "async" (Report tab), we don't want WITH_DOCLIST options
        if (exportMode === "async") {
          if (
            item.type === REPORT_EXPORT_TYPE.ASYNC_DOCX_WITH_DOCLIST ||
            item.type === REPORT_EXPORT_TYPE.ASYNC_PDF_WITH_DOCLIST
          ) {
            return false;
          }
        }

        return true;
      });
    } else {
      return EXPORT_LIST.filter((item) => {
        if (item.type === EXPORT_TYPE.EMAIL && isEmailExportDisabled) {
          return false;
        }
        return true;
      });
    }
  }, [content, exportMode, reportId]);

  const isDisabled = disabled || isLoadingContent || isExporting;

  return (
    <>
      <Button
        variant="ghost"
        size={buttonSize}
        onClick={handleCopyClick}
        disabled={isDisabled || isCopied}
        className={
          buttonSize === "mini"
            ? "text-foreground hover:text-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
            : undefined
        }
      >
        {isCopied ? (
          <>
            <CheckIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="common.copied" defaultMessage="Copied" />
          </>
        ) : isLoadingContent ? (
          <>
            <LoaderIcon className="w-4 h-4 mr-2 animate-spin" />
            <FormattedMessage id="common.copyAll" defaultMessage="Copy All" />
          </>
        ) : (
          <>
            <CopyIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="common.copyAll" defaultMessage="Copy All" />
          </>
        )}
      </Button>
      {hasDoclist ? (
        <>
          <Button
            variant="ghost"
            size={buttonSize}
            disabled={isDisabled}
            onClick={handleOpenExportModal}
          >
            <SquareArrowOutDownRightIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="common.export" defaultMessage="Export" />
          </Button>
          <ReportExportModal
            isOpen={isExportModalOpen}
            onClose={() => setIsExportModalOpen(false)}
            options={availableExportOptions}
            hasDoclist={hasDoclist}
            onExport={handleExport}
            isExporting={isExporting}
          />
        </>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isDisabled}>
            <Button variant="ghost" size={buttonSize} disabled={isDisabled}>
              <SquareArrowOutDownRightIcon className="w-4 h-4 mr-2" />
              <FormattedMessage id="common.export" defaultMessage="Export" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {availableExportOptions.map((item) => (
              <DropdownMenuItem
                key={item.type}
                onClick={() => handleDropdownExport(item.type)}
              >
                {intl.formatMessage({ id: `${item.label}.type` })}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <ExportDocListDialog
        isOpen={isExportDialogOpen}
        onClose={() => {
          setIsExportDialogOpen(false);
          setNameOfExportedFile("");
        }}
        fileName={nameOfExportedFile}
      />
    </>
  );
};
